import { useMessages } from '@hooks/useMessages';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownToggle,
  Spinner,
  UncontrolledDropdown,
} from 'reactstrap';
import { QrCodeIcon } from '@appcomponents/atoms/svg/QrCodeIcon';
import { useCompany } from '@data/store/CompanyProvider';
import scanQrCodeImg from '@src/assets/images/scan-qr.png';
import onScan from 'onscan.js';
import { logOnError } from '@src/@data/utility/errors';
import { useIsMobile } from '@src/@data/utility/isMobile';
import { getOS, OsPlatforms } from '@src/@data/utility/getOS';
import { BUSINESS_APP_APPLE_STORE_LINK } from '@appcomponents/atoms/AppleStoreButton';
import { BUSINESS_APP_GOOGLE_PLAY_LINK } from '@appcomponents/atoms/GooglePlayButton';
import QrScannerReservationItem from '@src/components/organisms/qr-scanner/QrScannerReservationItem';
import QrScannerBackButton from '@src/components/organisms/qr-scanner/QrScannerBackButton';
import {
  CodeType,
  parseScannedCode,
} from '@src/components/organisms/qr-scanner/parseScannedCode';
import QrScannerSubscriptionItem from '@src/components/organisms/qr-scanner/QrScannerSubscriptionItem';

export const QrScannerDropdown: React.FC = () => {
  const { selectedCompanyId } = useCompany();
  const { messages } = useMessages();
  const [isLoading, setIsLoading] = useState(false);

  const [reservationId, setReservationId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');

  const [error, setError] = useState<Error>();
  const isMobile = useIsMobile();

  const handleScan = useCallback(
    async (sCode: string) => {
      if (!selectedCompanyId) {
        return;
      }

      const parsedCode = parseScannedCode(sCode);

      if (!parsedCode) {
        const err = new Error(
          `Scanned QR code has no reservation or subscription id: `
        );
        logOnError(err);
        setError(err);
        setReservationId('');
        setSubscriptionId('');
        return;
      }

      if (parsedCode.type === CodeType.Reservation) {
        setReservationId(parsedCode.value);
        setSubscriptionId('');
      } else if (parsedCode.type === CodeType.Subscription) {
        setSubscriptionId(parsedCode.value);
        setReservationId('');
      }
      onScan.detachFrom(document);
    },
    [selectedCompanyId]
  );

  const keyCodeMapper = useCallback((e) => {
    // Allow reservation symbols to be parsed
    if (['/', '-'].includes(e.key)) {
      return e.key;
    }
    // Fall back to the default decoder in all other cases
    return onScan.decodeKeyEvent(e);
  }, []);

  const enableScanning = useCallback(() => {
    if (!onScan.isAttachedTo(document)) {
      onScan.attachTo(document, {
        reactToPaste: true,
        onScan: handleScan,
        keyCodeMapper,
      });
    }
    setIsLoading(true);
  }, [handleScan, keyCodeMapper]);

  const resetState = useCallback(() => {
    setReservationId('');
    setSubscriptionId('');
    setError(undefined);
    setIsLoading(false);
    if (onScan.isAttachedTo(document)) {
      onScan.detachFrom(document);
    }
  }, []);

  const businessAppLink = useMemo(() => {
    const os = getOS();
    if (os === OsPlatforms.IOS) {
      return BUSINESS_APP_APPLE_STORE_LINK;
    }
    if (os === OsPlatforms.Android) {
      return BUSINESS_APP_GOOGLE_PLAY_LINK;
    }
    return '#';
  }, []);

  if (!selectedCompanyId) {
    return null;
  }

  return (
    <UncontrolledDropdown
      tag="li"
      className="dropdown-notification nav-item mr-sm-1"
      onToggle={(e, isOpen) => (isOpen ? resetState() : undefined)}
    >
      <DropdownToggle color="transparent" className="d-flex align-items-center">
        <QrCodeIcon size={21} />
        <h5 className="ml-50 mb-0 dropdown-btn-text">
          {messages('nav_header_scan')}
        </h5>
      </DropdownToggle>
      <DropdownMenu right style={{ width: '300px' }}>
        {/* avoids runtime error when scan is done before scan button has been pressed */}
        <div role="menuitem" className="d-none" />
        {!reservationId && !subscriptionId && !error && (
          <div className="px-2 pb-1 text-center">
            <img src={scanQrCodeImg} alt="" className="w-100" />
            <h5>{messages('qr_scanner_dropdown_title')}</h5>
            <p>
              {isMobile
                ? messages('qr_scanner_dropdown_not_supported', {
                    businessApp: (
                      <a
                        href={businessAppLink}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        {messages(
                          'qr_scanner_dropdown_not_supported_business_app'
                        )}
                      </a>
                    ),
                  })
                : messages('qr_scanner_dropdown_description')}
            </p>
            {!isMobile && (
              <Button
                onClick={enableScanning}
                color="primary"
                size="sm"
                disabled={isLoading}
              >
                {isLoading && <Spinner size="sm" className="mr-50 mb-px" />}
                {messages('qr_scanner_dropdown_cta')}
              </Button>
            )}
          </div>
        )}
        {!isLoading && !!reservationId && (
          <QrScannerReservationItem
            reservationId={reservationId}
            companyId={selectedCompanyId}
            onBack={resetState}
          />
        )}
        {!isLoading && !!subscriptionId && (
          <QrScannerSubscriptionItem
            subscriptionId={subscriptionId}
            companyId={selectedCompanyId}
            onBack={resetState}
          />
        )}
        {!isLoading && !!error && (
          <>
            <QrScannerBackButton
              message={messages('qr_scanner_dropdown_reservation_not_found')}
              onBack={resetState}
            />
          </>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};
